import RootStore from './RootStore';
import axios from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import { EnvApi } from '../api/env.api';
import { AuthApi } from '../api/auth.api';
import { PartnerApi } from '../api/partner.api';
import { v4 as uuidv4 } from 'uuid';
import {
  getLocalStorageItem,
  LocalStorageKeyEnum,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../utils/local-storage-handler';
import { decodeBase64, encodeBase64 } from '../utils/string';

export default class AppStore {
  private rootStore: RootStore;
  private static readonly loginPageShowAllowHosts = [
    'localhost',
    'jpe2-caas1-prod3.caas.jpe2c.r-local.net',
    'stg-rmesse.commerce.rakuten.co.jp',
  ];
  private readonly loginPageShowAllow: boolean = false;

  @observable env = 'unknown';
  @observable pageTitle = '問い合わせ管理';
  @observable sessionId: string = '';
  @observable abTag: Record<string, any> = {};

  appVersion = {
    branch: process.env.REACT_APP_BRANCH_NAME,
    commit: process.env.REACT_APP_COMMIT,
  };

  @action
  isLoginPageShowAllowed(): boolean {
    return this.loginPageShowAllow;
  }

  @computed
  get disableSubmitMessage(): boolean {
    if (this.abTag.showSubmitMessage) {
      return false;
    }
    return true;
  }

  @computed
  get boldRecipientName(): boolean {
    if (this.abTag.boldRecipientName) {
      return true;
    }
    return false;
  }

  @action
  setEnv = (env: string) => {
    this.env = env;
  };

  @action
  setSessionId = (sessionId: string) => {
    this.sessionId = sessionId;
  };

  @action
  setAbTag = (abTag: Record<string, any>) => {
    this.abTag = abTag;
  };

  @action
  initApp = async (initialPathName: string, initialParam: string) => {
    axios.defaults.headers.common['client'] = `consumer`;
    this.initSessionId();
    if (
      initialPathName === '/login/dev' ||
      !initialPathName.match(/^\/(login|inquiry).*/)
    ) {
      return;
    }

    if (initialPathName === '/login') {
      const params =
        initialParam ||
        (await decodeBase64(getLocalStorageItem(LocalStorageKeyEnum.STATE)!));
      const paramsObj = new URLSearchParams(params);
      // check params
      if (
        paramsObj.get('serviceId') &&
        paramsObj.get('serviceAttribute') &&
        paramsObj.get('categoryId')
      ) {
        try {
          const basicInfo = await PartnerApi.getPartnerBasicInfo(
            Number(paramsObj.get('serviceId')),
            Number(paramsObj.get('serviceAttribute')),
            paramsObj.get('recipientId') || '',
          );
          if (
            basicInfo &&
            basicInfo.isActive[Number(paramsObj.get('categoryId'))] === false
          ) {
            const redirectDestination = `/inquiry${params}`;
            console.log(
              `this service is disabled. redirect to: ${redirectDestination}`,
            );
            window.location.href = redirectDestination;
            return;
          }
        } catch (e) {
          // just ignore the case
          console.error(e);
        }
      }
    }
    // for Omni auth flow, please see:
    // https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=3473725274
    if (
      !this.rootStore.loginStore.isDevLogin() &&
      !this.rootStore.omniLoginStore.hasOmniUpdateParameter()
    ) {
      await this.handlePreOmniAuthState(initialPathName, initialParam);
    } else {
      // to hide unnecessary parameter for Omni authentication,
      // remove "omni-update" parameter only and leave the other parameter like as "mode"
      this.rootStore.omniLoginStore.removeOmniUpdateParameter();
      await this.handlePostOmniAuthState(initialPathName);
    }
  };

  private handlePreOmniAuthState = async (path: string, param: string) => {
    // initialize local storage when visit from external domain
    if (path === '/login') {
      removeLocalStorageItem(LocalStorageKeyEnum.STATE);
    }

    // in case of reloading new inquiry page,
    // it redirects to /login with restored parameter by local storage
    if (path === '/inquiry/new') {
      if (getLocalStorageItem(LocalStorageKeyEnum.STATE)) {
        const redirectDestination = `/login${await decodeBase64(
          getLocalStorageItem(LocalStorageKeyEnum.STATE)!,
        )}`;
        console.log(
          `reloading new inquiry. redirect to: ${redirectDestination}`,
        );
        window.location.href = redirectDestination;
      } else {
        console.error('new inquiry page was reloaded with empty state');
        this.rootStore.inquiryPageStore.setShowOmniErrorPage(true);
      }
    }

    if (path === '/inquiry' && !param) {
      if (getLocalStorageItem(LocalStorageKeyEnum.STATE)) {
        const redirectDestination = `/inquiry${await decodeBase64(
          getLocalStorageItem(LocalStorageKeyEnum.STATE)!,
        )}`;
        console.log(
          `reloading inquiry list. redirect to: ${redirectDestination}`,
        );
        window.location.href = redirectDestination;
      } else {
        console.error('inquiry list page was reloaded with empty state');
        this.rootStore.inquiryPageStore.setShowOmniErrorPage(true);
      }
    }

    // TODO: Too much this kind of "special case" code...
    if (param && ['/inquiry/new', '/inquiry', '/login'].includes(path)) {
      // store encoded state for reloading new inquiry
      console.debug('path includes /inquiry or /login');
      setLocalStorageItem(LocalStorageKeyEnum.STATE, await encodeBase64(param));
    }

    await this.rootStore.omniLoginStore.startOmniAuthFlowAndAddOmniUpdateParameterAndRedirect(
      path,
      param,
    );
  };

  private handlePostOmniAuthState = async (path: string) => {
    this.rootStore.userStore.setTheLatestAuthToken();

    try {
      await this.initializePage(path);
    } catch (e) {
      console.error(e);
      window.location.href = '/50x';
      return;
    }

    this.rootStore.socketStore.initializeSocket();
  };

  @action
  initializePage = async (path: string) => {
    const res = await AuthApi.getUser();
    this.rootStore.initializePartnerState(res);

    await Promise.all([
      this.rootStore.appStore.getEnv(),
      this.rootStore.serviceStore.fetchServiceData(),
      this.rootStore.userStore.fetchUserData(res),
    ]);

    if (['/inquiry/new', '/inquiry/new/'].includes(path)) {
      const { inquiryTarget, recipients } = this.rootStore.userStore;
      // new inquiry page

      const targetRecipient = Array.from(recipients.values())[0];

      // create targetDetail
      if (inquiryTarget.targetId) {
        await this.rootStore.partnerStore?.setTargetDetail(
          targetRecipient,
          inquiryTarget.targetId,
        );
      }

      // create faq and initialize page
      await this.rootStore.faqStore.initializeFaqForNewInquiry(
        targetRecipient.serviceId,
        targetRecipient.serviceAttribute,
        targetRecipient.authId,
      );
      this.rootStore.inquiryStore.setActiveInquiryId(
        this.rootStore.inquiryStore.DUMMY_ID_FOR_NEW_INQUIRY_PAGE,
      );
    } else if (['/inquiry', '/inquiry/'].includes(path)) {
      // inquiry list page
      await this.rootStore.inquiryStore.fetchInquiryList();
    } else {
      // inquiry detail page
      await this.rootStore.inquiryStore.setActiveInquiryId(
        path.replace('/inquiry/', ''),
      );
    }
  };

  @action
  initSessionId = () => {
    const localSessionId = getLocalStorageItem(LocalStorageKeyEnum.SESSION_ID);
    if (localSessionId) {
      this.setSessionId(localSessionId);
    } else {
      const sessionId = uuidv4();
      this.setSessionId(sessionId);
      setLocalStorageItem(LocalStorageKeyEnum.SESSION_ID, sessionId);
    }
  };

  @action
  getEnv = async () => {
    const res = await EnvApi.getEnv(this.sessionId);
    this.setEnv(res.env);
    console.log('abTag:', res.abTag);
    if (res.abTag) {
      this.setAbTag(res.abTag);
    }
  };

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.loginPageShowAllow =
      !!window.location.hostname &&
      AppStore.loginPageShowAllowHosts.some((suffix) =>
        window.location.hostname.endsWith(suffix),
      );
    if (!this.loginPageShowAllow) {
      console.log = () => {
        return;
      };
    }
  }
}
